import { Dispatch, useEffect, useState } from "react";
import { Datepicker } from "@mobiscroll/react";
import { NavDropdown } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { TaskCategory } from "../../interfaces/task";
import FilterModal from "./FilterModel";
import { FilterCriteria } from "tasks-experience-client";
import { createTaskApi } from "../../api-helpers/TaskApiHelper";
import Lookups from "../../constants/Lookups";
import { Lookup, WorkflowState } from "common-util-experience-client";
import { GridDropdown } from "../../grid/GridDropdown";
import { WorkflowConstants } from "../../helper/WorkflowConstants";
import { GridWorkflowStatusDropdown } from "../../grid/GridWorkflowStatusDropdown";
import { GridLookupDropdown } from "../../grid/GridLookupDropdown";
import { GridUsersDropdown } from "../../grid/GridUsersDropdown";
import { UsersDto } from "users-experience-client";
import { createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";

interface TasksHeaderProps {
  patientId?: string;
  currentUserId: string;
  offCanvasShowCallback: (comp: string) => void;
  onUpdateFilters: (filterArray: FilterCriteria[]) => void;
  breadcrumbs: boolean | undefined;
  statuses:WorkflowState[] | null | undefined;
  setStatuses: Dispatch<WorkflowState[]>
}

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return [start, end];
};
const defaultDateRange = getCurrentMonthRange();

export const TasksHeader = (props: TasksHeaderProps) => {

  const tasksApi = createTaskApi();
  const { selectedCoach } = useParams<{ selectedCoach: string }>();

  const [searchText, setSearchText] = useState<string>();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [assignedTo, setAssignedTo] = useState<string[] | null>();
  const [categories, setCategories] = useState<TaskCategory[] | null>();
  const [priorities, setPriorities] = useState<string[]>([]);

  const [modalComp, setModalComp] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [initialStatusFilter,setInitialStatusFilter] = useState<string[]>([])

  // A patient's tasks list shows every assignee; the main Tasks page starts on
  // the current user's tasks unless the route asks for all coaches.
  useEffect(() => {
      props.patientId || selectedCoach === 'all' ? setAssignedTo([]) : setAssignedTo([props.currentUserId]);
  }, [props.currentUserId, props.patientId]);

  useEffect(()=>{
    (async()=>{
      const workflowApi = createWorkflowApi();
      const { data } = await workflowApi.workflowControllerGetStatesForWorkflow(WorkflowConstants.taskWorkflowType);
      const initFilter:string[] = [WorkflowConstants.open,WorkflowConstants.inProgress]
      const states = data.filter(state=>initFilter.includes(state.stateName))
      const ids = states.map(state=>state.stateId)
      setInitialStatusFilter([...ids])
      props.setStatuses([...states])
    })()
  },[])

  const updateFilters = () => {

    const filters: FilterCriteria[] = [];
    if (props.patientId) {
      filters.push({
        propertyName: "patientId",
        operator: "equals",
        propertyValue: props.patientId
      });
    }
    if ((searchText?.length ?? 0) > 0) {
      filters.push({
        propertyName: "taskSubtype",
        operator: "contains",
        propertyValue: searchText
      });
    }
    filters.push({
      propertyName: "dueAt",
      operator: "greater-than-equals",
      propertyValue: dateRange[0]
    });
    filters.push({
      propertyName: "dueAt",
      operator: "less-than-equals",
      propertyValue: dateRange[1]
    });
    if (assignedTo && assignedTo.length > 0) {
      filters.push({
        propertyName: "assignedUserId",
        operator: "in",
        propertyValue: assignedTo
      });
    }
    if (priorities && priorities.length > 0) {
      filters.push({
        propertyName: "priorityId",
        operator: "in",
        propertyValue: priorities
      });
    }
    if (categories && categories.length > 0) {
      filters.push({
        propertyName: "taskTypeId",
        operator: "in",
        propertyValue: categories.map(p => p.id)
      });
    }
    if (props.statuses && props.statuses.length > 0) {
      filters.push({
        propertyName: "wfStateId",
        operator: "in",
        propertyValue: props.statuses.map(p => p.stateId)
      });
    }
    props.onUpdateFilters(filters);
  }

  useEffect(updateFilters, [searchText, dateRange, assignedTo, props.statuses, categories, priorities]);

  
  const handleDateChange = (event: any) => {
    const [start, end] = event.value;
    if (!start || !end) {
      return;
    }
  
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
  
    const endDate = new Date(end);
    endDate.setHours(0, 0, 0, 0);
  
    setDateRange([startDate, endDate]);
  };

  const handleOffCanvasShow = (comp: string) => {
    props.offCanvasShowCallback(comp);
  };

  const handleModalShow = (comp: string) => {
    setModalTitle("Filter")
    setModalComp(comp);
    setModalShow(!modalShow);
  };

  const updatePriorityFilter = (values: Lookup[]) => {
    setPriorities(values.map(v => v.id));
  }

  const updateAssignedTo = (values: UsersDto[]) => {
    setAssignedTo(values ? values.map(v => v.id) : null);
  }

  const loadCategories = async (): Promise<TaskCategory[]> => {
    const response = await tasksApi.taskControllerFindAllTaskTypes();
    return response.data;
  };

  return (
    <>
      { props.breadcrumbs && <div
          className="d-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
          aria-label="breadcrumb"
        >
          <div className="flex-fill d-flex align-items-center">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item breadcrumb-item-first">
                  <Link to="/home">
                    <span className="material-symbols-outlined fs-14">
                      home
                    </span>
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Tasks
                </li>
              </ol>
              <h1 className="h2 m-0">Tasks</h1>
            </div>
          </div>

          <div className="justify-content-end d-flex align-items-center">
            <button
              className="btn btn-sm btn-outline-primary py-05 rounded-2 ms-6 d-none"
              onClick={() => handleModalShow("filterTask")}
            >
              <span className="material-symbols-outlined fs-16 align-text-top">
                filter_list
              </span>
            </button>
            <button
              className="btn btn-sm btn-secondary ms-md-3 px-3 mt-2 mt-md-0 rounded-pill pe-4"
              onClick={() => handleOffCanvasShow("addTasks")}
            >
              <span className="material-symbols-outlined fs-16 align-text-top">
                add
              </span> New Task
            </button>
          </div>
        </div> }

      <div
        className="d-md-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
      >
        <div className="flex-fill d-flex align-items-center">
          <div className="search-box max-w-md-18-75">
            <span className="material-symbols-outlined search-box__icon">
              search
            </span>
            <div className="typeahead-container search-box d-flex">
              <span className="material-symbols-outlined search-box__icon">
                search
              </span>
              <input
                type="text"
                name="searchTask"
                className="form-control search-box__input fw-normal fs-12"
                placeholder="Search Task"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center">
          
          <GridDropdown
            label="Task Type"
            keyProvider={(item) => item.id}
            labelProvider={(item) => item.name}
            dataProvider={loadCategories}
            onChange={setCategories}
          ></GridDropdown>

          <NavDropdown
            id="nav-dropdown-dark-example"
            title="Due Date"
            className={`btn-dropdown ms-md-2 me-2 me-md-0 mt-2 mt-md-0 ${dateRange.length ? 'border border-primary rounded-2' : 'border border-white'}`}
            >
            <div style={{ width: "500px" }}>
              <Datepicker
                controls={["calendar"]}
                select="range"
                calendarType="month"
                pages={2}
                touchUi={true}
                display="inline"
                value={dateRange}
                onChange={handleDateChange}
                theme="windows"
                themeVariant="light"
              />
            </div>
          </NavDropdown>

          <GridLookupDropdown
            label="Priority"
            type={Lookups.TASK_PRIORITY}
            onChange={updatePriorityFilter}
          ></GridLookupDropdown>

          <GridUsersDropdown
            label="Coach"
            onChange={updateAssignedTo}
            initialUserIds={assignedTo ?? []}
          ></GridUsersDropdown>

          <GridWorkflowStatusDropdown
            label="Status"
            type={WorkflowConstants.taskWorkflowType}
            onChange={props.setStatuses}
            initialStatusIds={initialStatusFilter}
          ></GridWorkflowStatusDropdown>

          <button
            className="btn btn-sm btn-outline-primary py-05 rounded-2 ms-6 d-none"
            onClick={() => handleModalShow("filterTask")}
          >
            <span className="material-symbols-outlined fs-16 align-text-top">
              filter_list
            </span>
          </button>

          {!props.breadcrumbs &&
            <button
              className="btn btn-sm btn-secondary ms-md-3 px-3 mt-2 mt-md-0 rounded-pill pe-4"
              onClick={() => handleOffCanvasShow("addTasks")}
            >
              <span className="material-symbols-outlined fs-16 align-text-top">
                add
              </span> New Task
            </button>}
        </div>
      </div>
      <FilterModal
        title={modalTitle}
        modalShow={modalShow}
        setModalShow={setModalShow}
        modalComp={modalComp}
        updateFilter={props.onUpdateFilters}
      ></FilterModal>
    </>
  );
};
